import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { UserCircle, ArrowCounterClockwise, Palette, Check, Sun, MoonStars, Sparkle } from "@phosphor-icons/react";
import { ProBadge } from "@/components/ProBadge";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { detectCurrency, PRICES, fmt, startCheckout } from "@/lib/billing";
import NotificationPrefs from "./NotificationPrefs";
import WearablesCard from "./WearablesCard";
import CoachMemoryCard from "./CoachMemoryCard";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const GOALS = [["lose_fat", "Lose fat"], ["build_muscle", "Build muscle"], ["get_stronger", "Get stronger"], ["improve_endurance", "Improve endurance"], ["longevity", "Longevity"], ["general_health", "General health"]];
const ACTIVITY = [["sedentary", "Sedentary"], ["light", "Light"], ["moderate", "Moderate"], ["active", "Active"], ["athlete", "Athlete"]];
const EXPERIENCE = [
  ["beginner", "Beginner — new to health & training, explain everything simply"],
  ["intermediate", "Intermediate — I know the basics"],
  ["advanced", "Advanced — give me the technical detail"],
];
const STRESS = [["low", "Low"], ["moderate", "Moderate"], ["high", "High"]];
const SEXES = [["male", "Male"], ["female", "Female"], ["other", "Other"]];
const LOCATIONS = [["home", "Home"], ["gym", "Gym"], ["office", "Office"], ["outdoor", "Outdoor"]];

const inputCls = "w-full bg-surface border border-border rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-brand transition-colors";

export default function ProfileTab({ profile = {}, onReload }) {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || "",
    goal: profile.goal || "general_health",
    experience_level: profile.experience_level || "intermediate",
    age: profile.age ?? "",
    sex: profile.sex || "other",
    height_cm: profile.height_cm ?? "",
    weight_kg: profile.weight_kg ?? "",
    activity_level: profile.activity_level || "moderate",
    training_days_per_week: profile.training_days_per_week ?? 3,
    typical_sleep_hours: profile.typical_sleep_hours ?? 7,
    stress_level: profile.stress_level || "moderate",
    usual_location: profile.usual_location || "home",
    injuries: (profile.injuries || []).join(", "),
    biggest_obstacle: profile.biggest_obstacle || "",
    north_star: profile.north_star || "",
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const p = {
        goal: form.goal,
        experience_level: form.experience_level,
        sex: form.sex,
        activity_level: form.activity_level,
        stress_level: form.stress_level,
        usual_location: form.usual_location,
        injuries: form.injuries.split(",").map((s) => s.trim()).filter(Boolean),
        biggest_obstacle: form.biggest_obstacle,
        north_star: form.north_star,
      };
      if (Number(form.age)) p.age = Number(form.age);
      if (Number(form.height_cm)) p.height_cm = Number(form.height_cm);
      if (Number(form.weight_kg)) p.weight_kg = Number(form.weight_kg);
      if (Number(form.training_days_per_week)) p.training_days_per_week = Number(form.training_days_per_week);
      if (Number(form.typical_sleep_hours)) p.typical_sleep_hours = Number(form.typical_sleep_hours);

      await api.put("/me/profile", { name: form.name, profile: p });
      toast.success("Profile updated — your plan will adapt");
      await checkAuth();
      onReload?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const restart = async () => {
    try {
      await api.post("/onboarding/restart");
      await checkAuth();
      navigate("/onboarding");
    } catch {
      toast.error("Could not restart onboarding");
    }
  };

  return (
    <div className="space-y-6">
      <div className="clay p-6 sm:p-8" data-testid="profile-card">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-brand/10 flex items-center justify-center">
            <UserCircle size={20} weight="fill" color="var(--brand)" />
          </div>
          <div>
            <h3 className="font-display text-lg font-medium">Your profile</h3>
            <p className="text-xs text-muted2">Ikavio uses this to shape every plan. Keep it current.</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mt-6">
          <Field label="Name">
            <input data-testid="profile-name-input" className={inputCls} value={form.name} onChange={set("name")} />
          </Field>
          <Field label="Primary goal">
            <Select testId="profile-goal-select" value={form.goal} onChange={set("goal")} options={GOALS} />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Experience level — how Ikavio talks to you">
              <Select testId="profile-experience-select" value={form.experience_level} onChange={set("experience_level")} options={EXPERIENCE} />
            </Field>
          </div>
          <Field label="Age">
            <input data-testid="profile-age-input" type="number" min="14" max="100" className={inputCls} value={form.age} onChange={set("age")} />
          </Field>
          <Field label="Biological sex">
            <Select testId="profile-sex-select" value={form.sex} onChange={set("sex")} options={SEXES} />
          </Field>
          <Field label="Height (cm)">
            <input data-testid="profile-height-input" type="number" className={inputCls} value={form.height_cm} onChange={set("height_cm")} />
          </Field>
          <Field label="Weight (kg)">
            <input data-testid="profile-weight-input" type="number" className={inputCls} value={form.weight_kg} onChange={set("weight_kg")} />
          </Field>
          <Field label="Activity level">
            <Select testId="profile-activity-select" value={form.activity_level} onChange={set("activity_level")} options={ACTIVITY} />
          </Field>
          <Field label="Training days / week">
            <input data-testid="profile-training-days-input" type="number" min="0" max="7" className={inputCls} value={form.training_days_per_week} onChange={set("training_days_per_week")} />
          </Field>
          <Field label="Typical sleep (hours)">
            <input data-testid="profile-sleep-input" type="number" step="0.5" min="3" max="12" className={inputCls} value={form.typical_sleep_hours} onChange={set("typical_sleep_hours")} />
          </Field>
          <Field label="Stress level">
            <Select testId="profile-stress-select" value={form.stress_level} onChange={set("stress_level")} options={STRESS} />
          </Field>
          <Field label="Usual training location">
            <Select testId="profile-location-select" value={form.usual_location} onChange={set("usual_location")} options={LOCATIONS} />
          </Field>
          <Field label="Injuries / limitations (comma separated)">
            <input data-testid="profile-injuries-input" className={inputCls} value={form.injuries} onChange={set("injuries")} placeholder="e.g. lower back, left knee" />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Biggest obstacle">
              <input data-testid="profile-obstacle-input" className={inputCls} value={form.biggest_obstacle} onChange={set("biggest_obstacle")} placeholder="e.g. late work nights" />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Your north star — who do you want to be in five years?">
              <textarea data-testid="profile-northstar-input" rows={2} className={inputCls} value={form.north_star} onChange={set("north_star")}
                placeholder="e.g. Strong and energetic enough to keep up with my kids, calm under pressure" />
            </Field>
          </div>
        </div>

        <button
          data-testid="save-profile-btn"
          onClick={save}
          disabled={saving}
          className="mt-6 bg-ink text-bg rounded-full px-6 py-2.5 text-sm font-medium hover:bg-brand transition-colors disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save profile"}
        </button>
      </div>

      <WearablesCard />

      {user?.is_admin && (
        <Link to="/admin" data-testid="admin-link"
          className="block clay p-5 text-sm font-medium text-brand hover:bg-secondary/40 transition-colors">
          Founder dashboard — revenue snapshot →
        </Link>
      )}

      <CoachMemoryCard />

      <PlanCard />

      <AppearanceCard />

      <NotificationPrefs />

      <div className="clay p-6 sm:p-8 flex items-center justify-between gap-4 flex-wrap" data-testid="reonboard-card">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-secondary flex items-center justify-center">
            <ArrowCounterClockwise size={20} weight="bold" color="var(--brand)" />
          </div>
          <div>
            <h3 className="font-display text-lg font-medium">Redo the onboarding interview</h3>
            <p className="text-xs text-muted2 max-w-sm">Life changed? Rebuild your Digital Twin from scratch with a fresh conversation.</p>
          </div>
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button data-testid="reonboard-btn" className="border border-border rounded-full px-5 py-2.5 text-sm font-medium hover:bg-secondary transition-colors">
              Start over
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent className="rounded-3xl">
            <AlertDialogHeader>
              <AlertDialogTitle>Redo onboarding?</AlertDialogTitle>
              <AlertDialogDescription>
                You'll have a fresh conversation with Ikavio to rebuild your profile. Your check-in history and Healthspan trend are kept.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="reonboard-cancel-btn" className="rounded-full">Cancel</AlertDialogCancel>
              <AlertDialogAction data-testid="reonboard-confirm-btn" onClick={restart} className="rounded-full bg-brand hover:bg-ink">
                Yes, start over
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

function PlanCard() {
  const { user } = useAuth();
  const ent = user?.entitlement || {};
  const cur = detectCurrency();
  const p = PRICES[cur];
  const [busy, setBusy] = useState(null);
  const go = async (plan) => { setBusy(plan); await startCheckout(plan); setBusy(null); };

  const FREE = ["Daily check-in & optimal day plan", "Healthspan Score & streaks", "Breathing tool & pressure-off days", "Glossary & beginner mode"];
  const PRO = ["Digital Twin — predictions, baselines & anomaly alerts", "Wearable integrations (Oura, Google Health, Apple Health)", "Ikavio Coach — unlimited AI coaching", "Weekly AI review", "Adaptive nutrition"];
  return (
    <div className="clay p-6 sm:p-8" data-testid="plan-card">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-2xl bg-brand/10 flex items-center justify-center">
          <Sparkle size={20} weight="fill" color="var(--brand)" />
        </div>
        <div className="flex-1 min-w-[200px]">
          <h3 className="font-display text-lg font-medium flex items-center gap-2">Your plan <ProBadge testId="plan-card-badge" /></h3>
          {ent.plan === "pro" ? (
            <p className="text-xs text-sage font-medium" data-testid="plan-status-pro">You're on Ikavio Pro ✓ — thank you for backing us.</p>
          ) : ent.trial_active ? (
            <p className="text-xs text-muted2" data-testid="plan-status-trial">
              You're on the full <span className="text-brand font-medium">7-day Pro trial</span> — everything unlocked until {new Date(ent.trial_ends_at).toLocaleDateString()}.
            </p>
          ) : (
            <p className="text-xs text-muted2" data-testid="plan-status-free">Your Pro trial has ended — you're on the Free plan.</p>
          )}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-5 mt-6">
        <div>
          <span className="label-eyebrow">Always free</span>
          <ul className="mt-2 space-y-1.5">
            {FREE.map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm text-muted2">
                <Check size={15} weight="bold" color="var(--sage)" className="mt-0.5 shrink-0" /> {f}
              </li>
            ))}
          </ul>
        </div>
        <div>
          <span className="label-eyebrow">Ikavio Pro</span>
          <ul className="mt-2 space-y-1.5">
            {PRO.map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm text-muted2">
                <Check size={15} weight="bold" color="var(--brand)" className="mt-0.5 shrink-0" /> {f}
              </li>
            ))}
          </ul>
        </div>
      </div>
      {ent.plan === "pro" && (
        <button
          data-testid="manage-subscription-btn"
          disabled={busy === "portal"}
          onClick={async () => {
            setBusy("portal");
            try {
              const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
              window.location.href = data.url;
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Could not open the billing portal");
              setBusy(null);
            }
          }}
          className="mt-6 border border-border rounded-full px-6 py-3 text-sm font-medium hover:bg-secondary transition-colors disabled:opacity-60"
        >
          {busy === "portal" ? "Opening portal…" : "Manage subscription — invoices, payment method, cancel"}
        </button>
      )}
      {ent.plan !== "pro" && (
        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <button
            data-testid="upgrade-monthly-btn"
            disabled={!!busy}
            onClick={() => go("monthly")}
            className="bg-brand text-white rounded-full px-6 py-3 text-sm font-medium hover:bg-ink hover:text-bg transition-colors disabled:opacity-60"
          >
            {busy === "monthly" ? "Opening checkout…" : <>Go Pro — first month {fmt(p.intro, cur)}, then {fmt(p.monthly, cur)}/mo</>}
          </button>
          <button
            data-testid="upgrade-annual-btn"
            disabled={!!busy}
            onClick={() => go("annual")}
            className="border border-border rounded-full px-6 py-3 text-sm font-medium hover:bg-secondary transition-colors disabled:opacity-60"
          >
            {busy === "annual" ? "Opening checkout…" : <>{fmt(p.annual, cur)}/year — 2 months free</>}
          </button>
        </div>
      )}
      <p className="text-[11px] text-muted2 mt-4">Cancel anytime. Safety features (pressure-off days, reach-out support) always stay free.</p>
    </div>
  );
}

function AppearanceCard() {
  const { theme, setTheme } = useTheme();
  const MODES = [
    { id: "light", name: "Light", desc: "Bright & clear", icon: Sun },
    { id: "dark", name: "Dark", desc: "Easy on the eyes", icon: MoonStars },
  ];
  return (
    <div className="clay p-6 sm:p-8" data-testid="appearance-card">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-2xl bg-brand/10 flex items-center justify-center">
          <Palette size={20} weight="fill" color="var(--brand)" />
        </div>
        <div>
          <h3 className="font-display text-lg font-medium">Appearance</h3>
          <p className="text-xs text-muted2">Light or dark — it follows you across devices.</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-6">
        {MODES.map((m) => (
          <button
            key={m.id}
            data-testid={`appearance-mode-${m.id}`}
            onClick={() => setTheme(m.id)}
            className={`flex items-center gap-3 rounded-2xl border px-4 py-3 text-left transition-colors ${
              theme === m.id ? "border-brand bg-brand/5" : "border-border hover:bg-secondary/60"
            }`}
          >
            <m.icon size={20} weight="fill" color={m.id === "dark" ? "var(--sleep)" : "var(--stress)"} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">{m.name}</div>
              <div className="text-xs text-muted2">{m.desc}</div>
            </div>
            {theme === m.id && <Check size={16} weight="bold" color="var(--brand)" />}
          </button>
        ))}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="label-eyebrow block mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function Select({ value, onChange, options, testId }) {
  return (
    <select data-testid={testId} value={value} onChange={onChange} className={inputCls}>
      {options.map(([v, l]) => (
        <option key={v} value={v}>{l}</option>
      ))}
    </select>
  );
}
